/**
 * Initiative Tracker API — thin entity wrappers around the shared authenticated `api` client.
 * All endpoints live behind the /tracker prefix on the same API Gateway, gated by the same
 * Cognito JWT authorizer as the rest of project-nimbus.
 */

import { api } from './api';

// Note: there is no tracker-specific peopleApi here — Initiative Tracker pages use
// project-nimbus's own `peopleApi` (from './api') as the single source of truth for people.

export const initiativesApi = {
  getAll: () => api.get<{ initiatives: any[] }>('/tracker/initiatives').then((r) => r.initiatives),
  getOne: (id: string) => api.get<{ initiative: any }>(`/tracker/initiatives/${id}`).then((r) => r.initiative),
  create: (data: any) => api.post<{ initiative: any }>('/tracker/initiatives', data).then((r) => r.initiative),
  update: (id: string, data: any) => api.put<{ initiative: any }>(`/tracker/initiatives/${id}`, data).then((r) => r.initiative),
  delete: (id: string) => api.delete(`/tracker/initiatives/${id}`),
  complete: (id: string, metricHandoffs: { metricId: string; bauOwnerId: string; cadence: string }[]) =>
    api.post<{ initiative: any }>(`/tracker/initiatives/${id}/complete`, { metricHandoffs }).then((r) => r.initiative),
  approveException: (id: string, exceptionId: string, approvedBy: string) =>
    api.put<{ initiative: any }>(`/tracker/initiatives/${id}/exceptions/${exceptionId}/approve`, { approvedBy }).then((r) => r.initiative),
  rejectException: (id: string, exceptionId: string, approvedBy: string) =>
    api.put<{ initiative: any }>(`/tracker/initiatives/${id}/exceptions/${exceptionId}/reject`, { approvedBy }).then((r) => r.initiative),
};

export const reportsApi = {
  list: (initiativeId: string) => api.get<{ reports: any[] }>(`/tracker/initiatives/${initiativeId}/reports`).then((r) => r.reports),
  create: (initiativeId: string, data: any) =>
    api.post<{ report: any }>(`/tracker/initiatives/${initiativeId}/reports`, data).then((r) => r.report),
  update: (initiativeId: string, reportId: string, data: any) =>
    api.put<{ report: any }>(`/tracker/initiatives/${initiativeId}/reports/${reportId}`, data).then((r) => r.report),
  delete: (initiativeId: string, reportId: string) => api.delete(`/tracker/initiatives/${initiativeId}/reports/${reportId}`),
  // Bedrock-drafted text only - never writes anything, the caller decides whether to use it.
  draft: (initiativeId: string, data: any) =>
    api.post<{ summary: string; blockers: string; nextSteps: string }>(`/tracker/initiatives/${initiativeId}/reports/draft`, data),
};

export const narrativeApi = {
  draft: (initiativeId: string, data: any) =>
    api.post<{ narrative: string }>(`/tracker/initiatives/${initiativeId}/narrative/draft`, data),
};

export const domainsApi = {
  getAll: () => api.get<{ domains: any[] }>('/tracker/domains').then((r) => r.domains),
  create: (data: any) => api.post<{ domain: any }>('/tracker/domains', data).then((r) => r.domain),
  update: (id: string, data: any) => api.put<{ domain: any }>(`/tracker/domains/${id}`, data).then((r) => r.domain),
  delete: (id: string) => api.delete(`/tracker/domains/${id}`),
};

export const servicesApi = {
  getAll: () => api.get<{ services: any[] }>('/tracker/services').then((r) => r.services),
  create: (data: any) => api.post<{ service: any }>('/tracker/services', data).then((r) => r.service),
  update: (id: string, data: any) => api.put<{ service: any }>(`/tracker/services/${id}`, data).then((r) => r.service),
  delete: (id: string) => api.delete(`/tracker/services/${id}`),
};

export const metricsApi = {
  getAll: () => api.get<{ metrics: any[] }>('/tracker/metrics').then((r) => r.metrics),
  create: (data: any) => api.post<{ metric: any }>('/tracker/metrics', data).then((r) => r.metric),
  update: (id: string, data: any) => api.put<{ metric: any }>(`/tracker/metrics/${id}`, data).then((r) => r.metric),
  delete: (id: string) => api.delete(`/tracker/metrics/${id}`),
};

export const readingsApi = {
  list: (metricId: string) => api.get<{ readings: any[] }>(`/tracker/metrics/${metricId}/readings`).then((r) => r.readings),
  create: (metricId: string, data: any) =>
    api.post<{ reading: any }>(`/tracker/metrics/${metricId}/readings`, data).then((r) => r.reading),
  update: (metricId: string, readingId: string, data: any) =>
    api.put<{ reading: any }>(`/tracker/metrics/${metricId}/readings/${readingId}`, data).then((r) => r.reading),
  delete: (metricId: string, readingId: string) => api.delete(`/tracker/metrics/${metricId}/readings/${readingId}`),
};
